import * as s from '~js/utils/selectors';
import * as a from '~js/utils/attributes';
import * as c from '~js/utils/classes';
import { a11y } from '~js/components/a11y';
import { throttle } from '~js/utils/utils';
import { Collapsible } from '~js/components/collapsible';
import { Tabs } from '~js/components/tabs';

export class Drawer {
  constructor(container) {
    this.container = container;
    this.drawers = this.container.querySelectorAll(s.selectors$S.drawer);
    this.drawerToggleButtons = this.container.querySelectorAll(s.selectors$S.drawerToggle);
    this.a11y = a11y;

    this.drawerToggleEvent = throttle((event) => {
      this.toggle(event);
    }, 150);

    this.keyPressCloseEvent = throttle((event) => {
      if (event.code === theme.keyboardKeys.ESCAPE) {
        this.close(event);
      }
    }, 150);

    // Define drawer close event
    this.drawerCloseEvent = (event) => {
      const activeDrawer = document.querySelector(`${s.selectors$S.drawer}.${c.classes$I.open}`);
      let isDrawerToggle = false;

      if (!activeDrawer) {
        return;
      }

      if (event.type === 'click') {
        isDrawerToggle = event.target.matches(s.selectors$S.drawerToggle);
      }
      const isDrawerChild = activeDrawer ? activeDrawer.contains(event.target) : false;
      const quickviewItem = activeDrawer.closest(s.selectors$S.quickviewItem);
      const isQuickviewChild = quickviewItem ? quickviewItem.contains(event.target) : false;

      if (!isDrawerToggle && !isDrawerChild && !isQuickviewChild) {
        this.close();
      }
    };

    this.initListeners();
  }

  initListeners() {
    // Toggle event for each drawer button
    this.drawerToggleButtons.forEach((button) => {
      button.addEventListener('click', this.drawerToggleEvent);
    });

    // Close drawers if escape key pressed
    this.drawers.forEach((drawer) => {
      drawer.addEventListener('keyup', this.keyPressCloseEvent);

      // Init collapsible mobile dropdowns
      this.collapsible = new Collapsible(drawer);
      this.tabs = new Tabs(drawer);
    });

    // Close drawers on click outside
    document.addEventListener('click', this.drawerCloseEvent);

    // Close drawers on closing event
    document.addEventListener('theme:drawer:closing', this.drawerCloseEvent);
  }

  toggle(e) {
    e.preventDefault();
    const drawer = document.querySelector(`#${e.target.getAttribute(a.attributes$A.ariaControls)}`);
    if (!drawer) {
      return;
    }

    const isDrawerOpen = drawer.classList.contains(c.classes$I.open);

    if (isDrawerOpen) {
      this.close();
    } else {
      this.open(e);
    }
  }

  open(e) {
    const drawerOpenButton = e.target;
    const drawer = document.querySelector(`#${e.target.getAttribute(a.attributes$A.ariaControls)}`);

    if (!drawer) {
      return;
    }
    const drawerScroller = drawer.querySelector(s.selectors$S.scroller) || drawer;

    // Disable page scroll right away
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: drawerScroller }));
    document.dispatchEvent(new CustomEvent('theme:drawer:open'), { bubbles: true });
    document.body.classList.add(c.classes$I.drawerOpen);

    drawer.classList.add(c.classes$I.open);
    drawer.classList.remove(c.classes$I.contentVisibilityHidden);
    drawerOpenButton.setAttribute(a.attributes$A.ariaExpanded, true);

    setTimeout(() => {
      this.a11y.state.trigger = drawerOpenButton;
      this.a11y.trapFocus({
        container: drawer,
      });
    });
  }

  close() {
    if (!document.body.classList.contains(c.classes$I.drawerOpen)) {
      return;
    }

    const drawer = document.querySelector(`${s.selectors$S.drawer}.${c.classes$I.open}`);

    this.drawerToggleButtons.forEach((button) => {
      button.setAttribute(a.attributes$A.ariaExpanded, false);
    });

    this.a11y.removeTrapFocus({
      container: drawer,
    });

    drawer.classList.remove(c.classes$I.open);

    const onDrawerTransitionEnd = (event) => {
      if (event.target !== drawer) return;

      requestAnimationFrame(() => {
        drawer.classList.add(c.classes$I.contentVisibilityHidden);
        document.dispatchEvent(new CustomEvent('theme:drawer:close'), { bubbles: true });
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      });

      drawer.removeEventListener('transitionend', onDrawerTransitionEnd);
    };

    drawer.addEventListener('transitionend', onDrawerTransitionEnd);

    document.body.classList.remove(c.classes$I.drawerOpen);
  }

  onUnload() {
    // Close drawer
    this.close();

    // Unbind all event listeners for drawers
    this.drawerToggleButtons.forEach((button) => {
      button.removeEventListener('click', this.drawerToggleEvent);
    });
    this.drawers.forEach((drawer) => {
      drawer.removeEventListener('keyup', this.keyPressCloseEvent);
    });
    document.removeEventListener('click', this.drawerCloseEvent);
    document.removeEventListener('theme:drawer:closing', this.drawerCloseEvent);

    if (this.collapsible) {
      this.collapsible.onUnload();
    }

    if (this.tabs) {
      this.tabs.onUnload();
    }
  }
}